import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

/**
 * Apigee error class that includes HTTP status code
 */
export class ApigeeError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public originalError: HttpErrorResponse,
    public detail?: string,
  ) {
    super(message);
    this.name = 'ApigeeError';
  }
}
/**
 * Centralized error handling for HTTP requests to Apigee
 * @param error - HTTP error response
 * @returns Observable that throws a formatted error
 */
export function handleError(error: HttpErrorResponse): Observable<never> {
  let message: string;
  let detail: string | undefined;

  const typedError = error.error as Record<string, unknown>;

  // Server-side error from Apigee - parse developerMessage for custom error messages
  if (typedError && typedError['developerMessage']) {
    try {
      const parsedMessage = JSON.parse(typedError['developerMessage'] as string) as {
        message?: string;
        error?: string;
      };
      message =
        parsedMessage.message || (typedError['userMessage'] as string | undefined) || `Server Error: ${error.status}`;
      detail = parsedMessage.error;
    } catch {
      message =
        (typedError['userMessage'] as string | undefined) ||
        (typedError['developerMessage'] as string | undefined) ||
        `Server Error: ${error.status}`;
    }
  }
  // Client-side/network error
  else if (error.error instanceof ErrorEvent) {
    message = `Network Error: ${error.error.message}`;
  }
  // Fallback
  else {
    message = error.message || `Server Error: ${error.status}`;
  }

  return throwError(() => new ApigeeError(message, error.status, error, detail));
}
